// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsVisionGetAllAttrs,
  IAboutUsVisionGetAttrs,
  IAboutUsVisionSaveAttrs,
} from 'interfaces/back-office/content-management/about-us/vision/about-us-vision-view.interface'
import {
  IAboutUsVisionApiGetAllResponse,
  IAboutUsVisionApiGetResponse,
  IAboutUsVisionApiPaginatedResponse,
  IAboutUsVisionApiSaveResponse,
} from 'interfaces/back-office/content-management/about-us/vision/about-us-vision-api.interface'
import { IAboutUsVisionDetail } from 'interfaces/back-office/content-management/about-us/vision'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// About Us Vision Props
const aboutUsVisionProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  content: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// About Us Vision List Model
const AboutUsVisions = types
  .model('AboutUsVisions')
  .props(withHelper_baseResponsePaginationTypes(types.model('AboutUsVisionList').props(aboutUsVisionProps)))

// About Us Vision Detail Model
const AboutUsVisionDetail = types.model('AboutUsVisionDetail').props(aboutUsVisionProps)

const AboutUsVisionStoreModel = types
  .model('AboutUsVision')
  .props({
    aboutUsVision_isLoading: types.optional(types.boolean, false),
    aboutUsVision_isSaveLoading: types.optional(types.boolean, false),
    aboutUsVision_aboutUsVisions: types.optional(AboutUsVisions, {}),
    aboutUsVision_detail: types.optional(AboutUsVisionDetail, {}),
  })
  .actions(self => ({
    aboutUsVision_SET_LOADING: (payload: boolean): void => {
      self.aboutUsVision_isLoading = payload
    },
    aboutUsVision_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsVision_isSaveLoading = payload
    },
    aboutUsVision_SET_ABOUT_US_VISIONS: (payload: IAboutUsVisionApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.aboutUsVision_aboutUsVisions = cast(mapPayload)
    },
    aboutUsVision_SET_ABOUT_US_VISION: (payload: IAboutUsVisionDetail): void => {
      self.aboutUsVision_detail = cast(payload)
    },
    aboutUsVision_CLEAR_ABOUT_US_VISIONS: (): void => {
      self.aboutUsVision_aboutUsVisions = cast({})
    },
    aboutUsVision_CLEAR_ABOUT_US_VISION: (): void => {
      self.aboutUsVision_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about Us Visions
     *
     * @param {IAboutUsVisionGetAllAttrs} payload
     *
     * @return {any} any
     */
    aboutUsVision_fetchAboutUsVisions: flow(function* (payload: IAboutUsVisionGetAllAttrs) {
      self.aboutUsVision_SET_LOADING(true)

      const response: IAboutUsVisionApiGetAllResponse = yield self.environment.aboutUsVisionApi.aboutUsVision_getAboutUsVisions({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.aboutUsVision_SET_ABOUT_US_VISIONS(response.res.data)

      self.aboutUsVision_SET_LOADING(false)
    }),

    /**
     * @description Fetch about us vision
     *
     * @param {IAboutUsVisionGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsVision_fetchAboutUsVision: flow(function* (payload: IAboutUsVisionGetAttrs) {
      self.aboutUsVision_SET_LOADING(true)

      const response: IAboutUsVisionApiGetResponse = yield self.environment.aboutUsVisionApi.aboutUsVision_getAboutUsVision({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.aboutUsVision_SET_ABOUT_US_VISION(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsVision_SET_LOADING(false)
    }),

    /**
     * @description Save about us vision
     *
     * @param {IAboutUsVisionSaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsVision_saveAboutUsVision: flow(function* (payload: IAboutUsVisionSaveAttrs) {
      self.aboutUsVision_SET_SAVE_LOADING(true)

      const response: IAboutUsVisionApiSaveResponse = yield self.environment.aboutUsVisionApi.aboutUsVision_saveAboutUsVision({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsVision_SET_SAVE_LOADING(false)
    }),
  }))

export const createAboutUsVisionStoreDefaultModel = () => types.optional(AboutUsVisionStoreModel, {})
